/**
 * Art 项目的结构化内容源。
 *
 * 旧站点里每个项目都是一个独立 HTML 页面；迁移到 React 后，
 * 项目详情页改为“同一个组件 + 不同数据”的方式渲染。
 *
 * 字段说明：
 * - slug: URL 中使用的项目标识，例如 /art/white-shirt。
 * - cover: 列表页和首页使用的封面图。
 * - video/poster/leadImage: 详情页顶部媒体资源，可按项目缺省。
 * - statement: 详情页 Statement 段落数组。
 * - galleryClass/figureClass: 复用原 styles.css 中不同项目的画廊布局类。
 * - images: 相对项目目录的图片文件名，ProjectPage 会拼成完整 URL。
 */
export const projects = [
  {
    slug: 'white-shirt',
    title: 'The White Shirt Project',
    year: '2022',
    location: 'New York',
    info: '2022 - New York - Garment / Water-soluble material / Performance',
    medium: 'Garment / Water-soluble material / Performance art / Performance',
    cover: '/assets/art-projects/white-shirt/cover.jpg',
    video: '/assets/art-projects/white-shirt/main.mp4',
    poster: '/assets/art-projects/white-shirt/hero-before-video.jpg',
    leadImage: '/assets/art-projects/white-shirt/hero-before-video.jpg',
    excerpt:
      'Restriction, desire, truthfulness, and the exposed body - staged through the dress shirt as a chosen form of restraint.',
    note: 'Restriction produces desire. The dress shirt appears here as chosen restraint, social performance, and a surface through which truthfulness insists on exposure.',
    statement: [
      'Restriction produces desire. The modern society is one that is driven by desire. We are building countless moral values to cover up desires deep inside our heart. The field of art has always been a place to escape from societal judgement.',
      'The dress shirt is the most fitting to represent this concept because a dress shirt, while being one of the most basic and inclusive clothing in the world of fashion, represents restraint: intentionally chosen to be restrained.',
      'The Emperor new clothes exposes falsehoods, hypocrisy, and blind belief. In modern society, honesty is less and less valued and is usually linked with vulnerability.',
      'I want to be the innocent child. I want to stay as the innocent child.'
    ],
    galleryClass: 'project-gallery--white-shirt-grid',
    figureClass: 'project-figure--white-shirt-grid',
    images: ['01.jpg', '02.jpg', '03.jpg', '04.jpg', '05.jpg']
  },
  {
    slug: 'restriction-freedom',
    title: 'Restriction and Freedom',
    year: '2022',
    location: 'New York',
    info: '2022 - New York - Garment / Performance art / Performance',
    medium: 'Garment / Performance art / Performance',
    cover: '/assets/art-projects/restriction-freedom/cover.jpg',
    video: '/assets/art-projects/restriction-freedom/main.mp4',
    poster: '/assets/art-projects/restriction-freedom/cover.jpg',
    leadImage: '/assets/art-projects/restriction-freedom/hero-before-video.jpg',
    excerpt:
      'A body meets force, resists, falls, and rises again - toward a state where restriction and freedom become indistinguishable.',
    note: 'A body enters relation with force, resists, falls, pauses, and stands again. Restriction becomes the condition through which freedom is perceived, pursued, and finally neutralized.',
    statement: [
      'We are born into a world of countless social expectations. At first, our awareness of these restrictions is not profound. As we grow, they become increasingly clear.',
      'The more we resist, the more visible they become. The more we resist, the more intense they become. Until we fall.',
      'After rigorous contemplation, I stand up again, finding a new way to coexist with restriction.',
      'Restriction and freedom coexist. Restriction creates the desire for freedom. Freedom, in turn, creates the desire for restriction.',
      'Restriction and freedom are, in essence, one. I dissolve the self, and exist together with all things.'
    ],
    galleryClass: 'project-gallery--restriction-grid',
    figureClass: 'project-figure--restriction-grid',
    images: ['06.jpg', '04.jpg', '03.jpg', '05.jpg', '07.jpg', '01.jpg', '02.jpg']
  },
  {
    slug: 'absurd-reality',
    title: 'Absurd. i. Reality',
    year: '2023',
    location: 'New York',
    info: '2023 - New York - Garment / Laser cutting / Performance art / Installation',
    medium: 'Garment / Laser cutting / Performance art / Installation',
    cover: '/assets/art-projects/absurd-reality/cover.jpg',
    leadImage: '/assets/art-projects/absurd-reality/main.jpg',
    excerpt:
      'A garment world shaped by internet anonymity, hollow individualism, and the absurdity of contemporary life at the edge of unrestricted freedom.',
    note: 'A work shaped by internet anonymity, overdeveloped individualism, and the absurd reality that emerges when freedom hardens into its own performance.',
    statement: [
      'Modern people only live in the modern. Individualism and free will have come to feel inherent, even as thought remains limited by social norms and commonsense morality.',
      'The unbridled development of individualism leads to the disintegration of society. Each individual is suggested to develop unique values and attach a label of individuality.',
      'This garment expresses the absurd reality that I experience, where absurdity has gradually become the norm of our lives.',
      'This work continues the cycle of restriction, freedom, and desire by representing the vertex of freedom.'
    ],
    galleryClass: 'project-gallery--absurd',
    figureClass: 'project-figure--absurd-half',
    images: [
      'after-09.jpg',
      'cover.jpg',
      'after-01.jpg',
      'after-02.jpg',
      'after-05.jpg',
      'after-04.jpg',
      'after-03.jpg',
      'after-06.jpg',
      'after-07.jpg'
    ]
  },
  {
    slug: 'the-bare-minimum',
    title: 'The Bare Minimum',
    year: '2023',
    location: 'New York',
    info: '2023 - New York - Garment / Multiform garment / Performance art / Performance',
    medium: 'Garment / Multiform garment / Performance art / Performance',
    cover: '/assets/art-projects/the-bare-minimum/cover.jpg',
    video: '/assets/art-projects/the-bare-minimum/main.mp4',
    poster: '/assets/art-projects/the-bare-minimum/cover.jpg',
    leadImage: '/assets/art-projects/the-bare-minimum/01.png',
    excerpt:
      'A project built around the social fiction of the bare minimum, where withdrawal, effort, and outcome collapse into the same artistic process.',
    note: 'A multiform garment and performance work about effort, refusal, minimum action, and the unstable social measurement of contribution.',
    statement: [
      'The bare minimum is never simply minimum. It is a social judgement, a moving threshold, and a quiet demand placed onto the body.',
      'This project treats withdrawal and effort as a shared process. A small movement can hold a large refusal.',
      'The garment shifts form as the body negotiates visibility, function, and exhaustion.'
    ],
    galleryClass: 'project-gallery--white-shirt-grid',
    figureClass: 'project-figure--white-shirt-grid',
    images: [
      '01.png',
      '02.png',
      '03.jpg',
      '04.jpg',
      '05.jpg',
      '06.png',
      '07.png',
      '08.png',
      '09.png'
    ]
  },
  {
    slug: 'after-survival',
    title: 'After Survival',
    year: '2023',
    location: 'New York',
    info: '2023 - New York - Garment / Installation art / Wearable sculpture / Performance art / Fashion film',
    medium: 'Garment / Installation art / Wearable sculpture / Performance art / Fashion film',
    cover: '/assets/art-projects/after-survival/cover.jpg',
    video: '/assets/art-projects/after-survival/main.mp4',
    poster: '/assets/art-projects/after-survival/cover.jpg',
    leadImage: '/assets/art-projects/after-survival/hero-before-video.jpg',
    excerpt:
      'A work about imbalance, anticipation, and self-protection in the psychic state that follows survival.',
    note: 'A work staged in the psychic space that follows survival, where anticipation, self-protection, imbalance, and rehearsal become both structure and prison.',
    statement: [
      'After survival refers to the societal state when a human society can easily meet the basic survival needs of most of its members.',
      'The most stable balance is not when both sides are equal, but when one side completely suppresses the other. Extremes, in themselves, are stable.',
      'Through anticipation and projection, I have mapped out precise paths and directions for myself. Every seemingly random action has been rehearsed countless times in my mind.',
      'I move forward with difficulty, trapped within the prison of my own thoughts. And yet, I know this is the best protection I can give myself.'
    ],
    galleryClass: 'project-gallery--after-survival',
    figureClass: 'project-figure--after-survival-third',
    images: [
      '01.jpg',
      '02.jpg',
      '03.jpg',
      '04.jpg',
      '05.jpg',
      '06.jpg',
      '07.jpg',
      '08.jpg',
      '09.jpg',
      '10.jpg',
      '10b.jpg',
      '11.jpg',
      '12.jpg',
      '13.jpg',
      '14.jpg',
      '15.jpg',
      '16.jpg',
      '17.jpg',
      '18.jpg',
      '19.jpg',
      '20.jpg'
    ]
  },
  {
    slug: 're-sculpting-inner-self',
    title: 'Re-sclupting the Inner Self',
    year: '2024',
    location: 'New York',
    info: '2024 - New York - Fashion / Sculpture / Performance art / Kinetic installation / Anti-fashion',
    medium: 'Fashion / Sculpture / Performance art / Kinetic installation / Anti-fashion',
    cover: '/assets/art-projects/re-sculpting-inner-self/cover.jpg',
    video: '/assets/art-projects/re-sculpting-inner-self/main.mp4',
    poster: '/assets/art-projects/re-sculpting-inner-self/cover.jpg',
    leadImage: '/assets/art-projects/re-sculpting-inner-self/hero-before-video.png',
    excerpt:
      'A project about reclaiming sincerity from the spectacle of fashion, breaking its polished facade so forgotten lives and fragmented selves can be seen.',
    note: 'A kinetic anti-fashion work that breaks polish open to reveal sincerity, fragmentation, and the body trying to reassemble itself.',
    statement: [
      'Fashion often turns sincerity into surface. This project reopens that surface and lets the neglected inner self become visible.',
      'The body appears as an unfinished sculpture, repeatedly adjusted by memory, protection, and the pressure to perform.',
      'To re-sculpt the inner self is not to complete it, but to keep it unstable enough to remain alive.'
    ],
    galleryClass: 'project-gallery--white-shirt-grid',
    figureClass: 'project-figure--white-shirt-grid',
    images: [
      '01.png',
      '02.png',
      '03.png',
      '04.png',
      '05.png',
      '06.png',
      '07.png',
      '08.png',
      '09.png',
      '10.png'
    ]
  },
  {
    slug: 'evacuate-from-meaning',
    title: 'Evacuate from Meaning',
    year: '2025',
    location: 'New York',
    info: '2025 - New York - Fashion / Performance / Installation art / Performance art / Sculpture / Water-soluble material / Stage prop',
    medium:
      'Fashion / Performance / Installation art / Performance art / Sculpture / Water-soluble material / Stage prop',
    cover: '/assets/art-projects/evacuate-from-meaning/cover.jpg',
    video: '/assets/art-projects/evacuate-from-meaning/main.mp4',
    poster: '/assets/art-projects/evacuate-from-meaning/cover.jpg',
    leadImage: '/assets/art-projects/evacuate-from-meaning/hero-before-video.jpg',
    excerpt:
      'A work that confronts freedom, restriction, and the absence of inherent meaning through transformation, performance, sculpture, and staged becoming.',
    note: 'The namesake work confronts freedom, restriction, and the absence of inherent meaning through staged transformation and material disappearance.',
    statement: [
      'Evacuate from Meaning is not built on symbols, narratives, or predefined values. It begins with a refusal to explain, to signify, to be read correctly.',
      'The work stages garments, bodies, objects, and soluble surfaces as conditions rather than answers.',
      'No definitions. No conclusions. Only continuous departure.'
    ],
    galleryClass: 'project-gallery--after-survival',
    figureClass: 'project-figure--after-survival-third',
    images: [
      '01.jpg',
      '02.jpg',
      '03.jpg',
      '04.jpg',
      '05.jpg',
      '06.jpg',
      '07.jpg',
      '08.jpg',
      '09.jpg',
      '10.jpg',
      '11.jpg',
      '12.jpg',
      '13.jpg',
      '14.jpg',
      '15.jpg',
      '16.jpg'
    ]
  }
];

// 路由详情页通过 slug 查找项目；找不到时页面会重定向回 /art。
export const getProjectBySlug = (slug) => projects.find((project) => project.slug === slug);
